import { TranslationToggle } from '@src/components/transcript/TranslationToggle';
import { useCaptionsLanguageApplier } from '@src/hooks/useCaptionsLanguageApplier';
import { useLanguageRemovalFallback } from '@src/hooks/useLanguageRemovalFallback';
import { useTranslationStoreHydration } from '@src/hooks/useTranslationStoreHydration';
import { useKaraokeStore } from '@src/stores-v2';
import {
  isOriginalLang,
  ORIGINAL_LANGUAGE,
} from '@src/utility/captionLanguage';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';

interface Props {
  editor: IVideoEditor | null;
}

/**
 * PLAT-9621 — Captions panel header language toggle.
 *
 * Renders next to the "Captions" panel title. Reads/writes the language
 * on `karaokeStore.defaultKaraokeConfig` so reopening the panel for the
 * same clip restores the prior selection. On change:
 *
 *  1. Updates `KaraokeConfig.language` (persisted on the config)
 *  2. Re-produces the caption track via `commandProduceKaraoke`
 *     — the producer routes to the phrase-boundary path when language is
 *     a translation, falls back to original character-length segmentation
 *     when `'original'`.
 *  3. Dispatches the `arbor-captions-language-changed` window event so
 *     LeftSidePanel's transcript view follows the captions language.
 *
 * On mount, syncs the captions language to the transcript view language
 * (`?lang=`). PLAT-10788 — with per-language persistence a switch loads the
 * saved variant instead of clobbering it, so the sync is lossless and needs no
 * override confirmation.
 */
export const CaptionsLanguageHeaderToggle: React.FC<Props> = observer(
  ({ editor }) => {
    const karaokeStore = useKaraokeStore();
    const supportedLanguages = useTranslationStoreHydration();
    const [searchParams] = useSearchParams();
    const transcriptLang = searchParams.get('lang');

    // Per-mount guard so the initial transcript→captions sync runs once;
    // later user picks flow through `onChange`.
    const respondedRef = useRef(false);

    const config = karaokeStore.defaultKaraokeConfig;
    const language = config?.language || ORIGINAL_LANGUAGE;

    // PLAT-9990: shared with the left transcript picker via
    // `useCaptionsLanguageApplier`. This surface forces subtitles
    // visible (the captions panel is captions-centric) and dispatches
    // the global event so the LeftSidePanel can mirror the selection in
    // its URL.
    const applyAndPropagate = useCaptionsLanguageApplier(editor, {
      showSubtitlesOnApply: true,
      dispatchGlobalEvent: true,
    });

    // On captions panel mount, follow the transcript view language (`?lang=`).
    // `applyAndPropagate` → `switchCaptionLanguage` loads the cached variant
    // when one exists (produces only on a miss) and no-ops when already on that
    // language (PLAT-10788), so this is safe to call unconditionally once.
    useEffect(() => {
      if (respondedRef.current) return;
      if (!editor || !config || supportedLanguages.length === 0) return;
      respondedRef.current = true;
      applyAndPropagate(transcriptLang ?? ORIGINAL_LANGUAGE);
    }, [
      editor,
      config,
      supportedLanguages.length,
      transcriptLang,
      applyAndPropagate,
    ]);

    const isOriginal = isOriginalLang(language);

    // PLAT-9700: fall back to Original when the active translation is
    // removed. `applyAndPropagate(ORIGINAL_LANGUAGE)` re-produces the
    // karaoke track in the source language and broadcasts the captions
    // language change event — same path as picking Original from the
    // dropdown. Hook MUST run before the early return so its subscriber
    // identity is stable across renders (React hook ordering).
    useLanguageRemovalFallback(isOriginal ? null : language, () =>
      applyAndPropagate(ORIGINAL_LANGUAGE),
    );

    if (!editor || !config || supportedLanguages.length === 0) return null;

    const onChange = (lang: string | null) => {
      // PLAT-10816 — a user's own pick gets an undo entry; the mount effect and
      // removal fallback above share this applier and deliberately do not.
      applyAndPropagate(lang ?? ORIGINAL_LANGUAGE, {
        createUndoRedoEntry: true,
      });
    };

    return (
      <Wrapper data-testid="captions-translation-toggle">
        {/*
         * The "Captions" label used to live here. It now renders as the
         * panel title in `RightSidePanel` (parallel to "Video Templates",
         * "Speakers", etc.) so the heading shows even before this toggle
         * is mountable (no editor / no config / no supported languages).
         */}
        <TranslationToggle
          selectedLanguage={isOriginal ? null : language}
          onLanguageChange={onChange}
          onRequestGenerate={() => {
            // Generation lives on the transcript panel (PLAT-9619). The
            // captions panel is a consumer only; the toggle hides the
            // "+ Add language" affordance via `generateDisabled`.
          }}
          generateDisabled
          supportedLanguages={supportedLanguages}
          surface="creator-studio-captions"
        />
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
